import {
  computed,
  Directive,
  ElementRef,
  inject,
  input,
  effect,
  afterNextRender,
  Injector,
  untracked,
  DestroyRef,
  DOCUMENT, EffectCleanupRegisterFn
} from '@angular/core';
import { isObservable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InteractivityService } from '../../services/interactivity-service';
import type { Observable } from 'rxjs';
import type { InputSignal } from '@angular/core';

export enum FocusPosition {
  TOP = 'top'
}

@Directive({
  selector: '[tdWbFocus]',
  exportAs: 'tdWbFocus',
  host: {
    '[style.position]': 'positionStyle()',
    '[style.top]': 'topStyle()',
    '[style.left]': 'leftStyle()'
  }
})
export class FocusDirective {

  readonly focus: InputSignal<boolean | Observable<unknown>> = input<boolean | Observable<unknown>>(false, {
    alias: 'tdWbFocus'
  });

  readonly focusPosition: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'tdWbFocusPosition' });
  /**
   * {boolean} default false, i.e., focus on the current element even if it's not tabbable.
   * If true, focus on the current element if it's tabbable, otherwise focus on first tabbable children.
   */
  readonly tdWbFocusCheckTabbable: InputSignal<boolean> = input<boolean>(false);
  readonly tdWbFocusDelay: InputSignal<number | undefined> = input<number | undefined>(undefined);

  readonly positionStyle = computed(() => this.focusPosition() === FocusPosition.TOP ? 'absolute' : null);
  readonly topStyle = computed(() => this.focusPosition() === FocusPosition.TOP ? '0' : null);
  readonly leftStyle = computed(() => this.focusPosition() === FocusPosition.TOP ? '0' : null);

  private readonly elementRef = inject(ElementRef);
  private readonly interactivityService = inject(InteractivityService);
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    // effect: subscribes to an Observable focus trigger or imperatively calls focus() —
    // afterNextRender + HTMLElement.focus() are browser APIs; onCleanup unsubscribes
    // if the input changes from an Observable to a boolean between re-runs.
    effect((onCleanup: EffectCleanupRegisterFn) => {
      const focusVal = this.focus();

      if (isObservable(focusVal)) {
        const subscription = focusVal.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((): void => {
          this.doFocus();
        });
        onCleanup(() => subscription.unsubscribe());
      } else if (focusVal) {
        untracked(() => this.doFocus());
      }
    });
  }

  private doFocus(): void {
    afterNextRender(() => {
      const delay = this.tdWbFocusDelay();
      if (delay === undefined) {
        this.doFocusNow();
      } else {
        setTimeout(() => {
          this.doFocusNow();
        }, delay);
      }
    }, { injector: this.injector });
  }

  private doFocusNow(): void {
    const hostElement = this.elementRef.nativeElement;
    if (!hostElement) {
      return;
    }
    const elementRef = this.tdWbFocusCheckTabbable() ?
      this.interactivityService.getFirstTabbableElement(hostElement) :
      hostElement;
    if (elementRef && typeof elementRef.focus === 'function' && elementRef !== this.document.activeElement) {
      elementRef.focus();
    }
  }
}
